import { EdgerRollPass } from "@/pages/catalogs/models/EdgerRoll";
import { PassesSectionsModal } from "@/pages/catalogs/pages/edger-rolls/components/passes/EditSectionsModal";
import {
	getPassColor,
	PassCircle,
} from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import { upsertEdgerRollPass } from "@/pages/catalogs/repositories/EdgerRollRepository";
import { ErrorModal } from "@dexteel/mesf-core";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import {
	alpha,
	Box,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type Props = {
	partId: number;
	passes: EdgerRollPass[];
	sectionId?: number | null;
	disabled?: boolean;
};

export const RollPassesEditor = ({
	partId,
	passes,
	sectionId,
	disabled = false,
}: Props) => {
	const theme = useTheme();
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [editingPass, setEditingPass] = useState<EdgerRollPass | null>(null);

	const upsertMutation = useMutation(
		({
			status,
			passNumber,
			partId,
			sectionIds,
		}: {
			status?: string;
			passNumber?: number;
			partId: number;
			sectionIds?: string;
		}) =>
			upsertEdgerRollPass({
				status: status?.charAt(0),
				passNumber,
				partId,
				sectionIds,
			}),
		{
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ["passes"] });
				queryClient.invalidateQueries({ queryKey: ["rolls"] });
				setEditingPass(null);
			},
			onError: (err: Error) => {
				setError(err.message);
			},
		},
	);

	const handleStatusClick = (pass: EdgerRollPass) => {
		if (disabled) return;

		const status =
			pass.PassStatusCode === "R"
				? "U"
				: pass.PassStatusCode === "U"
					? "D"
					: "R";

		upsertMutation.mutate({
			status,
			passNumber: pass.PassNumber ?? 0,
			partId: partId ?? 0,
		});
	};

	const handleSaveSections = (selectedSectionIds: number[]) => {
		if (!editingPass) return;
		upsertMutation.mutate({
			passNumber: editingPass.PassNumber ?? 0,
			partId: partId ?? 0,
			sectionIds: selectedSectionIds.join(","),
		});
	};

	const checkSectionMatch = (pass: EdgerRollPass): boolean | null => {
		if (!sectionId) return null;
		return pass.Sections?.some((s) => s.SectionId === sectionId) ?? false;
	};

	if (!passes || passes.length === 0) {
		return (
			<Typography
				variant="caption"
				sx={{ color: "text.secondary", fontStyle: "italic" }}
			>
				No passes configured
			</Typography>
		);
	}

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
						fontSize: "0.65rem",
						textTransform: "uppercase",
						fontWeight: 600,
					}}
				>
					Passes
				</Typography>
				<Stack spacing={0.5}>
					{passes.map((pass) => {
						const sectionMatch = checkSectionMatch(pass);
						const statusColor = getPassColor(pass.PassStatusCode, theme);

						return (
							<Box
								key={pass.PassNumber}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									p: 0.5,
									borderRadius: 1,
									bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
									"&:hover": {
										bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
									},
								}}
							>
								<Tooltip
									title={`Pass ${pass.PassNumber}: ${pass.StatusDescription}. Click to toggle.`}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											cursor: disabled ? "default" : "pointer",
										}}
										onClick={() => handleStatusClick(pass)}
									>
										<PassCircle
											firstColor={statusColor.main}
											sx={{
												fontSize: 18,
												transition: "transform 0.1s ease",
												"&:hover": disabled
													? {}
													: {
															transform: "scale(1.15)",
														},
											}}
										/>
									</Box>
								</Tooltip>

								<Box sx={{ flex: 1, minWidth: 0, maxWidth: 120 }}>
									<Typography
										variant="caption"
										sx={{
											fontSize: "0.7rem",
											color: "text.primary",
											fontWeight: 500,
										}}
									>
										Pass {pass.PassNumber}
									</Typography>
									<Tooltip
										title={
											pass.Sections.map((s) => s.SectionName).join(", ") ||
											"No sections"
										}
									>
										<Typography
											variant="caption"
											sx={{
												display: "block",
												fontSize: "0.6rem",
												color: "text.secondary",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												maxWidth: "100%",
											}}
										>
											{pass.Sections.map((s) => s.SectionName).join(", ") ||
												"No sections"}
										</Typography>
									</Tooltip>
								</Box>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										ml: "auto",
										mr: 1,
									}}
								>
									<Tooltip title="Edit sections">
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setEditingPass(pass);
											}}
											sx={{ p: 0.25 }}
											disabled={disabled}
										>
											<EditIcon sx={{ fontSize: 14 }} />
										</IconButton>
									</Tooltip>

									{sectionMatch !== null && (
										<Tooltip
											title={
												sectionMatch
													? "Pass matches current section"
													: "Pass does not match current section"
											}
										>
											{sectionMatch ? (
												<CheckCircleIcon
													sx={{ fontSize: 16, color: "success.main" }}
												/>
											) : (
												<CancelIcon
													sx={{ fontSize: 16, color: "error.main" }}
												/>
											)}
										</Tooltip>
									)}
								</Box>
							</Box>
						);
					})}
				</Stack>
			</Box>

			<PassesSectionsModal
				open={!!editingPass}
				onClose={() => setEditingPass(null)}
				onSave={handleSaveSections}
				currentSectionIds={
					editingPass?.Sections.map((s) => s.SectionId).join(",") ?? null
				}
				passNumber={editingPass?.PassNumber ?? null}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
